import { Component } from '@theme/component';
import { isMobileBreakpoint, throttle, debounce } from '@theme/utilities';

export class MobileDockComponent extends Component {
  /** @type {boolean} */
  #initialized = false;
  /** Scroll Y (px) past which the dock may show when scrolling down — equals mobile header height. @type {number | undefined} */
  #scrollPastHeaderThreshold;
  /** Previous `window.scrollY` to detect scroll direction. @type {number} */
  #previousScrollY = 0;
  /** @type {HTMLElement | null} */
  #section = null;
  /** @type {(() => void) | null} */
  #loadedOnFirstScroll = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number} */
  #lastMeasuredHeight = 0;
  /** @type {number | null} */
  #heightMeasurementFrame = null;
  /** Cached full document height; refreshed by the ResizeObserver, not per scroll. @type {number} */
  #documentHeight = 0;

  get section() {
    if (!this.#section) {
      this.#section = this.closest('.shopify-section');
    }
    return this.#section;
  }

  connectedCallback() {
    super.connectedCallback();
    this._handleResize = debounce(() => this.#init(), 50);
    window.addEventListener('resize', this._handleResize);
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this._handleResize);
    this._handleResize?.cancel?.();

    if (this.#loadedOnFirstScroll) {
      window.removeEventListener('scroll', this.#loadedOnFirstScroll);
      this.#loadedOnFirstScroll = null;
    }
    if (this.scrollHandlerHeader) {
      window.removeEventListener('scroll', this.scrollHandlerHeader);
      this.scrollHandlerHeader = null;
    }
    if (this.scrollHandlerFooter) {
      window.removeEventListener('scroll', this.scrollHandlerFooter);
      this.scrollHandlerFooter = null;
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    if (this.#heightMeasurementFrame !== null) {
      cancelAnimationFrame(this.#heightMeasurementFrame);
      this.#heightMeasurementFrame = null;
    }
    if (this.section) {
      this.section.removeEventListener('shopify:section:select', this.#onSectionSelect);
      this.section.removeEventListener('shopify:section:deselect', this.#onSectionDeselect);
    }
  }

  /**
   * Initializes the dock on mobile. Uses event.matches when called from media query change to avoid race.
   * @param {boolean} [isMobile] - Current mobile state; omit to use isMobileBreakpoint() (e.g. on init).
   */
  #init(isMobile = isMobileBreakpoint()) {
    if (!isMobile) return;
    if (this.#initialized) return;
    this.#initialized = true;
    this.#setupHeaderScroll();
    this.#setupFooterScroll();
    this.#scheduleLoadedClass();
    this.#scheduleHeightMeasurement();
    this.#observeHeight();

    if (Shopify.designMode && this.section) {
      this.section.addEventListener('shopify:section:select', this.#onSectionSelect);
      this.section.addEventListener('shopify:section:deselect', this.#onSectionDeselect);
    }
  }

  /** Configures dock visibility based on mobile header height. */
  #setupHeaderScroll() {
    const headerHeight = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height')
    );

    this.#scrollPastHeaderThreshold = Math.round(
      headerHeight || 100
    );

    this.scrollHandlerHeader = throttle(this.#handleHeaderScroll.bind(this));
    window.addEventListener('scroll', this.scrollHandlerHeader, { passive: true });
  }

  /** Scroll handler: adds/removes 'active' on the dock when scroll passes the header bottom; removes on scroll up. */
  #handleHeaderScroll() {
    if (!isMobileBreakpoint()) return;
    const currentScrollY = window.scrollY;
    const scrollingUp = currentScrollY < this.#previousScrollY;
    this.#previousScrollY = currentScrollY;

    if (scrollingUp || currentScrollY <= 0) {
      this.classList.remove('active');
      return;
    }
    if (currentScrollY >= this.#scrollPastHeaderThreshold) {
      this.classList.add('active');
    } else {
      this.classList.remove('active');
    }
  }

  /** Registers scroll listener to show dock when user reaches the bottom of the page (e.g. near footer). */
  #setupFooterScroll() {
    const footer = document.querySelector('footer');
    if (footer === null) return;

    this.scrollHandlerFooter = throttle(this.#handleFooterScroll.bind(this));
    window.addEventListener('scroll', this.scrollHandlerFooter, { passive: true });
  }

  /** Scroll handler: toggles 'active' when viewport is within threshold of the page bottom (runs after header logic). */
  #handleFooterScroll() {
    if (!isMobileBreakpoint()) return;

    // Use cached geometry so the per-scroll handler never reads offsetHeight/scrollHeight,
    // which would force a synchronous reflow after #handleHeaderScroll mutates classList.
    const scrolledTo = window.scrollY + window.innerHeight;
    const threshold = this.#lastMeasuredHeight;
    const isReachBottom = this.#documentHeight - threshold <= scrolledTo;
    this.classList.toggle('bottom-active', isReachBottom);
  }

  /** Add .loaded (transition) after first scroll — layout is stable by then, avoids iOS flicker. */
  #scheduleLoadedClass() {
    this.#loadedOnFirstScroll = () => {
      if (!isMobileBreakpoint() || this.classList.contains('loaded')) return;
      this.classList.add('loaded');
      window.removeEventListener('scroll', this.#loadedOnFirstScroll);
      this.#loadedOnFirstScroll = null;
    };
    window.addEventListener('scroll', this.#loadedOnFirstScroll, { passive: true });
  }

  #applyHeight() {
    const height = this.getBoundingClientRect().height;
    if (height <= 0 || height === this.#lastMeasuredHeight) return;

    this.#lastMeasuredHeight = height;
    document.documentElement.style.setProperty('--mobile-dock-height', `${height}px`);
  }

  #scheduleHeightMeasurement() {
    if (this.#heightMeasurementFrame !== null) {
      cancelAnimationFrame(this.#heightMeasurementFrame);
    }

    this.#heightMeasurementFrame = requestAnimationFrame(() => {
      this.#heightMeasurementFrame = null;
      this.#applyHeight();
    });
  }

  #observeHeight() {
    if (this.#resizeObserver) return;

    this.#resizeObserver = new ResizeObserver(() => {
      this.#scheduleHeightMeasurement();
      // Reading scrollHeight inside the RO callback runs after layout, so it is not a
      // forced reflow; caching it keeps #handleFooterScroll read-free.
      this.#documentHeight = document.body.scrollHeight;
    });

    // Observe the dock (its own height) and the body (full document height for footer detection).
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(document.body);
  }

  /** Theme editor: marks section as active when selected. */
  #onSectionSelect = () => this.section?.classList.add('shopify-active');
  /** Theme editor: removes active state when section is deselected. */
  #onSectionDeselect = () => this.section?.classList.remove('shopify-active');
}

if (!customElements.get('mobile-dock')) {
  customElements.define('mobile-dock', MobileDockComponent);
}
